import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { business, faqs } from '../config/siteData';
import { SectionHeading } from './Sections';

export function ServiceArea() {
  return (
    <section className="service-area section" id="service-area">
      <div className="map-visual">
        <div className="map-ring ring-one" />
        <div className="map-ring ring-two" />
        <div className="map-pin">
          <SafeIcon icon={FiIcons.FiMapPin} />
          <span>Austin, Texas</span>
        </div>
      </div>
      <div className="area-copy">
        <span className="eyebrow">Local expertise</span>
        <h2>Proudly Serving Austin &amp; Surrounding Areas</h2>
        <p>Serving short-term rental owners throughout Austin and surrounding communities. Contact us to see if your property is within our service area.</p>
        <a className="button" href="#quote">Check My Property</a>
      </div>
    </section>
  );
}

export function FAQ() {
  const [active, setActive] = useState(0);

  return (
    <section className="faq section" id="faq">
      <SectionHeading eyebrow="Frequently asked questions" title="Everything You Need to Know." centered />
      <div className="faq-list">
        {faqs.map(([question, answer], index) => {
          const isOpen = active === index;
          return (
            <article className={`faq-item ${isOpen ? 'open' : ''}`} key={question}>
              <button type="button" onClick={() => setActive(isOpen ? -1 : index)} aria-expanded={isOpen}>
                <span>{question}</span>
                <SafeIcon icon={isOpen ? FiIcons.FiMinus : FiIcons.FiPlus} />
              </button>
              <div className="faq-answer"><p>{answer}</p></div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  address: '',
  bedrooms: '',
  bathrooms: '',
  guests: '',
  propertyType: '',
  turnovers: '',
  startDate: '',
  details: '',
  consent: false
};

export function QuoteForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);

  const update = ({ target }) => {
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setForm((current) => ({ ...current, [target.name]: value }));
  };

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    ['fullName', 'email', 'phone', 'address', 'propertyType'].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = 'Required';
    });
    if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = 'Enter a valid email';
    if (!form.consent) nextErrors.consent = 'Consent is required';
    setErrors(nextErrors);
    setValidated(Object.keys(nextErrors).length === 0);
  };

  return (
    <section className="quote section" id="quote">
      <div className="quote-intro">
        <span className="eyebrow">Request a turnover quote</span>
        <h2>Let's Get Your Property Guest-Ready.</h2>
        <p>Tell us about your rental and the support you need. Your information is validated in the browser, ready for a future secure backend connection.</p>
        <div className="contact-list">
          <a href={`tel:${business.phone.replace(/\D/g, '')}`}><SafeIcon icon={FiIcons.FiPhone} /> {business.phone}</a>
          <a href={`mailto:${business.email}`}><SafeIcon icon={FiIcons.FiMail} /> {business.email}</a>
          <span><SafeIcon icon={FiIcons.FiMapPin} /> {business.serviceArea}</span>
        </div>
      </div>
      <form className="quote-form" onSubmit={submit} noValidate>
        <div data-greta-form-placeholder="TURNOVER_QUOTE">This form is not collecting yet.</div>
        <div className="form-grid">
          <Field label="Full Name" name="fullName" value={form.fullName} onChange={update} error={errors.fullName} />
          <Field label="Email" name="email" type="email" value={form.email} onChange={update} error={errors.email} />
          <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={update} error={errors.phone} />
          <Field label="Property Address" name="address" value={form.address} onChange={update} error={errors.address} />
          <Field label="Number of Bedrooms" name="bedrooms" type="number" value={form.bedrooms} onChange={update} />
          <Field label="Number of Bathrooms" name="bathrooms" type="number" step="0.5" value={form.bathrooms} onChange={update} />
          <Field label="Average Guests" name="guests" type="number" value={form.guests} onChange={update} />
          <label className="field">
            <span>Property Type</span>
            <select name="propertyType" value={form.propertyType} onChange={update}>
              <option value="">Select property type</option>
              <option>House</option>
              <option>Apartment / Condo</option>
              <option>Townhome</option>
              <option>Guesthouse</option>
              <option>Other</option>
            </select>
            {errors.propertyType && <small>{errors.propertyType}</small>}
          </label>
          <Field label="Approximate Turnovers Per Month" name="turnovers" type="number" value={form.turnovers} onChange={update} />
          <Field label="Preferred Service Start Date" name="startDate" type="date" value={form.startDate} onChange={update} />
          <label className="field field-full">
            <span>Additional Details</span>
            <textarea name="details" rows="5" value={form.details} onChange={update} placeholder="Tell us about your property, schedule and priorities." />
          </label>
        </div>
        <label className="consent">
          <input type="checkbox" name="consent" checked={form.consent} onChange={update} />
          <span>I agree to be contacted about Cleaning Bees services.</span>
        </label>
        {errors.consent && <small className="form-error">{errors.consent}</small>}
        {validated && (
          <motion.div className="backend-notice" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <SafeIcon icon={FiIcons.FiInfo} />
            <span>Your details are valid, but have not been sent or stored. Connect Supabase, a CRM, or an email service to activate submissions.</span>
          </motion.div>
        )}
        <button className="button submit-button" type="submit">
          Request a Quote <SafeIcon icon={FiIcons.FiArrowRight} />
        </button>
      </form>
    </section>
  );
}

function Field({ label, error, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input {...props} />
      {error && <small>{error}</small>}
    </label>
  );
}